import { useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_TOWN_SQUARE_URL } from '~/utils/townSquareLinks';
import {
  STEP_OPENS_VALUES,
  enabledWeightTotal,
  isDayBreakStepCode,
  nextDayBreakCode,
  type StepOpens,
} from '@arthelper/shared/quests/step-catalog';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowLeft, ExternalLink, GripVertical, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@artstorefronts/ui';
import { DYNAMIC_MESSAGE_STEP_CODES } from '@arthelper/shared/bounties/kind';
import { QUEST_PALETTE, QUEST_PALETTE_VALUES, questPaletteFor } from '@arthelper/shared/quests/quest-palette';
import AdminLayout from '../../../components/AdminLayout';
import { MemberPicker, type PickedMember } from '../bounties/MemberPicker';
import { QUEST_TYPES, questTypeEmoji } from '@arthelper/shared/quests/quest-types';
import {
  ADMIN_INVITE_TO_QUEST,
  ADMIN_QUEST,
  ADMIN_QUEST_STEP_CATALOG,
  ADMIN_ROTATE_QUEST_INVITE,
  ADMIN_SAVE_QUEST_STEPS,
  ADMIN_UPDATE_QUEST,
  type AdminQuest,
  listToText,
  questLandingUrl,
  questInviteUrl,
  questOptInUrl,
  type QuestStepCatalogEntry,
  textToList,
} from './quest-shared';
import { ChannelRow, ReminderSelect, type Reminder } from '../delivery-channels';

/**
 * The quest editor — the pre-feeds editor shape: a flat all-strings settings form with
 * dirty tracking, a dnd-kit sortable step list, one fixed bottom save bar firing
 * update-then-save-steps.
 *
 * The live "weights total" readout is the part that earns its place: the score is the sum
 * of the enabled weights, so 100 is an invariant, and a silent 95 would ship a quest whose
 * bar can never fill.
 */

interface SettingsForm {
  name: string;
  slug: string;
  heading: string;
  awardTitle: string;
  intents: string;
  careerStages: string;
  countryCodes: string;
  priority: string;
  gateStepCode: string;
  pointsReward: string;
  questType: string;
  emoji: string;
  color: string;
  assignment: string;
  directoryVisible: boolean;
  cadence: string;
  channelNotify: boolean;
  channelPin: boolean;
  channelEmail: boolean;
  notifyReminder: Reminder | null;
  emailReminder: Reminder | null;
}

interface StepRow {
  key: string;
  stepCode: string;
  weight: string;
  enabled: boolean;
  /**
   * Per-quest copy and link overrides. Round-tripped, not derived: this editor used to
   * read none and send none, and because saving steps DESTROYS and recreates every row,
   * any override a quest carried was wiped the next time someone pressed Save.
   */
  overrides: Record<string, string>;
}

/** Exactly what the server accepts; the resolver rejects anything else by name. */
const OVERRIDE_FIELDS = [
  { key: 'label', label: 'Label', hint: 'The line the member reads on the band.' },
  { key: 'evidence', label: 'Evidence', hint: 'The one-line proof under it.' },
  { key: 'why', label: 'Why', hint: 'The longer sell, shown when the step is opened.' },
  { key: 'href', label: 'Link', hint: 'Where the step goes. Relative for our own pages.' },
  { key: 'minutes', label: 'Minutes', hint: 'How long it claims to take.' },
] as const;

/**
 * What a tap can do. Every value here must exist in STEP_OPENS_VALUES — the server
 * rejects anything else — so this list is derived from it rather than retyped. It had
 * drifted both ways: it offered `explainer`, which is not a real value and which the
 * server would have refused, and omitted six that are.
 */
const OPENS_LABELS: Record<StepOpens, string> = {
  'artwork-modal': 'Add-artwork modal (in place)',
  'new-tab': 'Leave the app (another origin only)',
  embed: 'Embed the destination (in place)',
  'comment-targets': 'People picker — comment (in place)',
  'like-targets': 'People picker — like (in place)',
  'dormant-note': 'Note composer — one artist, one piece (in place)',
  arty: 'Arty panel — needs a sparklink URL',
  'artist-matches': 'Suggested connections (in place)',
  invite: 'Invite-a-friend modal (in place)',
  'critique-post': 'Ask for a critique (in place)',
  'compose-post': 'Community post composer (same tab)',
  'greet-targets': 'People picker — greet a newcomer (in place)',
  'dormant-targets': 'People picker — bring somebody back (in place)',
  'critique-answer': 'Critique picker — answer an ask (in place)',
  'rating-answer': 'Rating picker — score a piece (in place)',
  'pricing-answer': 'Pricing picker — price a piece (in place)',
  'poll-answer': 'Poll picker — vote (in place)',
};

const OPENS_CHOICES = [
  { value: '', label: 'Default for this step' },
  ...STEP_OPENS_VALUES.map((value) => ({ value, label: OPENS_LABELS[value] })),
] as const;

/**
 * The award card, as the member's profile will render it.
 *
 * Built here rather than imported from town-square: this is the client app and cannot
 * import across that workspace boundary. The name is a stand-in — what an admin needs to
 * judge is the award LINE at its real size, not whose award it is.
 */
function questAwardPreviewUrl(awardTitle: string): string {
  const base = __TOWN_SQUARE_URL__ || DEFAULT_TOWN_SQUARE_URL;
  const params = new URLSearchParams({ name: 'An artist', quest: awardTitle || 'Untitled quest' });
  return `${base}/api/quest-share-card?${params.toString()}`;
}

function isCustomRow(stepCode: string): boolean {
  return stepCode.startsWith('custom:');
}

function toForm(quest: AdminQuest): SettingsForm {
  return {
    name: quest.name,
    slug: quest.slug,
    heading: quest.heading,
    awardTitle: quest.awardTitle ?? '',
    intents: listToText(quest.audience?.intents),
    careerStages: listToText(quest.audience?.careerStages),
    countryCodes: listToText(quest.audience?.countryCodes),
    priority: String(quest.priority),
    gateStepCode: quest.gateStepCode ?? '',
    pointsReward: String(quest.pointsReward),
    questType: quest.questType || 'general',
    emoji: quest.emoji ?? '',
    // A colour since retired from the palette would come back on every save and be
    // refused, so the quest could never be saved; it falls back to the slug's own colour.
    color: QUEST_PALETTE_VALUES.includes(quest.color ?? '') ? (quest.color as string) : '',
    assignment: quest.assignment || 'auto',
    directoryVisible: quest.directoryVisible !== false,
    cadence: quest.cadence || 'once',
    channelNotify: Boolean(quest.channelNotify),
    channelPin: quest.channelPin !== false,
    channelEmail: Boolean(quest.channelEmail),
    notifyReminder: (quest.notifyReminder as Reminder | null) ?? null,
    emailReminder: (quest.emailReminder as Reminder | null) ?? null,
  };
}

/** Drops blanks and coerces `minutes`; `null` when nothing is overridden at all. */
function toConfigOverrides(overrides: Record<string, string>): Record<string, string | number> | null {
  const entries = Object.entries(overrides)
    .filter(([, value]) => value.trim() !== '')
    .map(([field, value]) => [field, field === 'minutes' ? Number(value) || 0 : value] as const);
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

/**
 * The people a step sends her at, by the picker it opens. Only the mutually exclusive
 * pools get a badge — the point is to stop a quest mixing them, not to label every step.
 */
const POOL_BADGE: Record<string, { label: string; className: string; title: string }> = {
  'dormant-targets': {
    label: 'LAPSED',
    className: 'bg-orange-100 text-orange-800',
    title: 'Targets members who have not been here in 14-90 days. Disjoint from the newcomer pool.',
  },
  'greet-targets': {
    label: 'NEWCOMERS',
    className: 'bg-sky-100 text-sky-800',
    title: 'Targets members who joined in the last 14 days. Disjoint from the lapsed pool.',
  },
};

function toRows(quest: AdminQuest): StepRow[] {
  return [...quest.steps]
    .sort((a, b) => a.position - b.position)
    .map((step) => ({
      key: step.stepCode,
      stepCode: step.stepCode,
      weight: String(step.weight),
      enabled: step.enabled,
      overrides: Object.fromEntries(
        Object.entries(step.configOverrides ?? {}).map(([field, value]) => [field, String(value)])
      ),
    }));
}

function SortableStepRow({
  row,
  catalog,
  gate,
  onWeight,
  onToggle,
  onRemove,
  onOverride,
}: {
  row: StepRow;
  catalog: Map<string, QuestStepCatalogEntry>;
  gate: boolean;
  onWeight: (key: string, weight: string) => void;
  onToggle: (key: string, enabled: boolean) => void;
  onRemove: (key: string) => void;
  onOverride: (key: string, field: string, value: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: row.key });
  const entry = catalog.get(row.stepCode);
  const overrideCount = Object.values(row.overrides).filter((value) => value.trim() !== '').length;
  const [editing, setEditing] = useState(false);

  /**
   * A DAY BREAK, not a step. It rides the same sortable list — that is the whole reason
   * it is a `quest_steps` row — so it drags and reorders for free, but it has no weight,
   * no overrides, no measurement and nothing to enable.
   */
  if (isDayBreakStepCode(row.stepCode)) {
    return (
      <div
        ref={setNodeRef}
        style={{ transform: CSS.Transform.toString(transform), transition }}
        className="flex items-center gap-3 rounded-lg border border-dashed border-indigo-300 bg-indigo-50/60 px-3 py-2"
      >
        <button type="button" {...attributes} {...listeners} className="cursor-grab text-indigo-400">
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="flex-1 text-sm font-medium text-indigo-900">
          Day break — everything below opens the next day
        </span>
        <Button variant="ghost" size="icon" onClick={() => onRemove(row.key)} aria-label="Remove day break">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="rounded-lg border bg-white px-3 py-2"
    >
      <div className="flex items-center gap-3">
        <button type="button" {...attributes} {...listeners} className="cursor-grab text-gray-400">
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {entry?.adminLabel ?? row.stepCode}
            {gate ? (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800">GATE</span>
            ) : null}
            {entry?.completion === 'click' ? (
              <span
                className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-800"
                title="Checks itself when the member clicks through to the tool. The work happens somewhere we cannot read back, so opening it IS the completion."
              >
                ON CLICK
              </span>
            ) : (
              <span
                className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-700"
                title="Checks itself from the member's real data. It cannot be ticked by hand, and it un-checks if the data changes."
              >
                MEASURED
              </span>
            )}
            {/* The window is half the measurement and the member feels it immediately, so
                it gets a badge rather than living only in the truncated line below. */}
            {/* WHICH POOL THIS STEP TARGETS. A quest named "bring people back" was
                composed out of greet* steps, whose pool is DISJOINT from the lapsed one
                by construction — so it could not be completed by reviving anybody, and
                its picker opened on an empty newcomer list (Nick, 2026-09-26). The
                measurement line below says so in prose; at a glance, nobody read it. */}
            {POOL_BADGE[entry?.defaultOpens ?? ''] ? (
              <span
                className={`ml-2 rounded px-1.5 py-0.5 text-[10px] ${POOL_BADGE[entry!.defaultOpens]!.className}`}
                title={POOL_BADGE[entry!.defaultOpens]!.title}
              >
                {POOL_BADGE[entry!.defaultOpens]!.label}
              </span>
            ) : null}
            {entry?.scope === 'since-join' ? (
              <span
                className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-800"
                title="Counts only what the member does AFTER this quest starts for them. Anything they did before joining is ignored."
              >
                SINCE JOINING
              </span>
            ) : null}
          </p>
          <p className="truncate text-xs text-gray-500">{entry?.adminMeasurement ?? 'unknown step code'}</p>
        </div>
        <Input
          className="w-16 text-right"
          value={row.weight}
          onChange={(event) => onWeight(row.key, event.target.value)}
          aria-label={`Weight for ${row.stepCode}`}
        />
        <Switch checked={row.enabled} onCheckedChange={(checked) => onToggle(row.key, checked)} />
        {/* Default size, so this matches the other admin Buttons exactly (they all render
          `h-7`, 28px). That is below the 44px touch floor, but it is the admin surface's
          own convention for a desktop-only tool — an outlier button here would read as
          broken, and re-speccing admin sizing is not this change's job. */}
        <Button
          variant="ghost"
          onClick={() => setEditing((open) => !open)}
          aria-label={`Edit wording for ${row.stepCode}`}
          title="Override this step's wording and link for this quest only"
        >
          {overrideCount > 0 ? `Edited (${overrideCount})` : 'Edit wording'}
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onRemove(row.key)} aria-label={`Remove ${row.stepCode}`}>
          <Trash2 className="h-4 w-4 text-gray-400" />
        </Button>
      </div>

      {editing ? (
        <div className="mt-3 space-y-2 border-t pt-3">
          <p className="text-xs text-gray-500">
            These change this step <strong>on this quest only</strong> — the catalog default is untouched everywhere
            else. Leave a box empty to use the default.
          </p>
          {OVERRIDE_FIELDS.map((field) => (
            <label key={field.key} className="block">
              <span className="text-xs font-medium text-gray-700">{field.label}</span>
              <Input
                value={row.overrides[field.key] ?? ''}
                onChange={(event) => onOverride(row.key, field.key, event.target.value)}
                placeholder={defaultFor(entry, field.key) || field.hint}
                aria-label={`${field.label} override for ${row.stepCode}`}
              />
            </label>
          ))}
          {isCustomRow(row.stepCode) ? (
            <>
              <label className="block">
                <span className="text-xs font-medium text-gray-700">What the tap does</span>
                <select
                  className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm"
                  value={row.overrides.opens ?? ''}
                  onChange={(event) => onOverride(row.key, 'opens', event.target.value)}
                  aria-label={`Behaviour for ${row.stepCode}`}
                >
                  {OPENS_CHOICES.map((choice) => (
                    <option key={choice.value} value={choice.value}>
                      {choice.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Borrow a measurement (optional)</span>
                <Input
                  value={row.overrides.measures ?? ''}
                  onChange={(event) => onOverride(row.key, 'measures', event.target.value)}
                  placeholder="e.g. followArtists — leave empty to complete on click"
                  aria-label={`Borrowed measurement for ${row.stepCode}`}
                />
                <span className="mt-1 block text-xs text-gray-500">
                  A custom step completes when the member clicks it. Name another step here to borrow its measurement
                  instead, and it completes from her real data.
                </span>
              </label>
            </>
          ) : null}

          {row.overrides.label?.trim() ? (
            <p className="text-xs text-amber-700">
              Heads up: several catalog labels count out loud (&ldquo;3 of 12&rdquo;). A fixed label replaces that
              counter with static text.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Placeholder = what the member sees if this box is left empty. */
function defaultFor(entry: QuestStepCatalogEntry | undefined, field: string): string {
  if (!entry) return '';
  if (field === 'label') return entry.defaultLabel ?? '';
  if (field === 'evidence') return entry.defaultEvidence ?? '';
  if (field === 'why') return entry.defaultWhy ?? '';
  if (field === 'href') return entry.defaultHref ?? '';
  if (field === 'minutes') return entry.defaultMinutes != null ? String(entry.defaultMinutes) : '';
  return '';
}

export default function AdminQuestEditor() {
  const { id } = useParams<{ id: string }>();
  const { data, loading } = useQuery<{ adminQuest: AdminQuest }>(ADMIN_QUEST, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
    skip: !id,
  });
  const { data: catalogData } = useQuery<{ adminQuestStepCatalog: QuestStepCatalogEntry[] }>(ADMIN_QUEST_STEP_CATALOG);
  const [updateQuest, { loading: savingSettings }] = useMutation(ADMIN_UPDATE_QUEST);
  const [saveSteps, { loading: savingSteps }] = useMutation(ADMIN_SAVE_QUEST_STEPS);

  const quest = data?.adminQuest ?? null;
  const catalog = useMemo(
    () => new Map((catalogData?.adminQuestStepCatalog ?? []).map((entry) => [entry.code, entry])),
    [catalogData]
  );

  const [form, setForm] = useState<SettingsForm | null>(null);
  const [rows, setRows] = useState<StepRow[]>([]);
  const [savedForm, setSavedForm] = useState<SettingsForm | null>(null);
  const [savedRows, setSavedRows] = useState<StepRow[]>([]);
  const [addCode, setAddCode] = useState('');
  const isDirtyRef = useRef(false);

  const hasUnsavedChanges =
    form !== null &&
    (JSON.stringify(form) !== JSON.stringify(savedForm) || JSON.stringify(rows) !== JSON.stringify(savedRows));
  isDirtyRef.current = hasUnsavedChanges;

  useEffect(() => {
    if (!quest || isDirtyRef.current) return;
    const nextForm = toForm(quest);
    const nextRows = toRows(quest);
    setForm(nextForm);
    setRows(nextRows);
    setSavedForm(nextForm);
    setSavedRows(nextRows);
  }, [quest]);

  // Generic over the key so each field gets ITS own type: `directoryVisible` is a
  // boolean, and a blanket `string` silently type-errored the build while every
  // string-valued caller kept compiling.
  const setField = <K extends keyof SettingsForm>(field: K, value: SettingsForm[K]) =>
    setForm((current) => (current ? { ...current, [field]: value } : current));

  // A divider is a position in the list, not a step: it carries no weight, so counting
  // it would make adding one to a valid quest read as 0/100 and refuse to go live.
  const enabledTotal = enabledWeightTotal(rows);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows((current) => {
      const oldIndex = current.findIndex((row) => row.key === active.id);
      const newIndex = current.findIndex((row) => row.key === over.id);
      return arrayMove(current, oldIndex, newIndex);
    });
  };

  const handleSave = async () => {
    if (!quest || !form) return;
    try {
      await updateQuest({
        variables: {
          id: quest.id,
          input: {
            name: form.name,
            heading: form.heading,
            awardTitle: form.awardTitle.trim() || null,
            audience:
              form.assignment !== 'auto'
                ? { intents: [], careerStages: [], countryCodes: [] }
                : {
                    intents: textToList(form.intents),
                    careerStages: textToList(form.careerStages),
                    countryCodes: textToList(form.countryCodes),
                  },
            priority: Number(form.priority) || 0,
            gateStepCode: form.gateStepCode || null,
            pointsReward: Number(form.pointsReward) || 0,
            questType: form.questType,
            emoji: form.emoji.trim() || null,
            color: form.color || null,
            assignment: form.assignment,
            directoryVisible: form.directoryVisible,
            cadence: form.cadence,
            channelNotify: form.channelNotify,
            channelPin: form.channelPin,
            channelEmail: form.channelEmail,
            notifyReminder: form.notifyReminder,
            emailReminder: form.emailReminder,
          },
        },
      });
      await saveSteps({
        variables: {
          questId: quest.id,
          steps: rows.map((row, index) => ({
            stepCode: row.stepCode,
            position: index + 1,
            weight: Number(row.weight) || 0,
            enabled: row.enabled,
            // Empty string means "unset", not "override with blank" — otherwise clearing
            // a box would blank the step's copy instead of restoring the catalog default.
            configOverrides: toConfigOverrides(row.overrides),
          })),
        },
      });
      setSavedForm(form);
      setSavedRows(rows);
      toast.success('Quest saved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save quest');
    }
  };

  if (loading && !quest) {
    return (
      <AdminLayout title="Quest" description="">
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  if (!quest || !form) {
    return (
      <AdminLayout title="Quest" description="">
        <p className="py-8 text-sm text-gray-500">Quest not found.</p>
      </AdminLayout>
    );
  }

  // `custom` is never offered here: it is not a step you add, it is a step you write,
  // and the button beside this picker mints it with its own code. A message whose words are
  // picked on delivery (a platform highlight, a hot thread, the welcome gift) is never a
  // quest step; it stays in the catalog so a quest that already holds one still reads its
  // label.
  const availableToAdd = (catalogData?.adminQuestStepCatalog ?? [])
    .filter((entry) => entry.code !== 'custom' && !DYNAMIC_MESSAGE_STEP_CODES.includes(entry.code))
    .filter((entry) => !rows.some((row) => row.stepCode === entry.code));

  // The quest's identity as the member will see it — chosen pastel, or the stable
  // slug-hashed fallback.
  const identityPalette = questPaletteFor(form.slug, form.color || null);
  return (
    <AdminLayout
      title={quest.name}
      description={`Status: ${quest.status} · Opt-in: ${questOptInUrl(form.slug)}`}
      action={
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/admin/quests">
              <ArrowLeft className="mr-2 h-4 w-4" /> All quests
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <a
              href={questLandingUrl(form.slug)}
              target="_blank"
              rel="noopener noreferrer"
              title={
                quest.status === 'LIVE' ? 'Open the live quest page' : 'Preview the quest page (admins only until live)'
              }
            >
              <ExternalLink className="mr-2 h-4 w-4" /> {quest.status === 'LIVE' ? 'View live page' : 'Preview'}
            </a>
          </Button>
        </div>
      }
    >
      <div className="grid gap-8 pb-24 lg:grid-cols-2">
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Settings</h2>
          <div className="space-y-1">
            <Label htmlFor="q-name">Name</Label>
            <Input id="q-name" value={form.name} onChange={(e) => setField('name', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Slug (set at creation — the URL never changes)</Label>
            <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-600">
              /quests/{form.slug}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="q-heading">Heading (what the member sees on the band)</Label>
            <Input id="q-heading" value={form.heading} onChange={(e) => setField('heading', e.target.value)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="q-emoji">Icon</Label>
              <Input
                id="q-emoji"
                value={form.emoji}
                maxLength={4}
                onChange={(e) => setField('emoji', e.target.value)}
                placeholder="Emoji"
              />
              <p className="text-xs text-gray-500">Blank = the quest type&rsquo;s default icon.</p>
            </div>
            <div className="space-y-1">
              <Label>Color</Label>
              <div className="flex flex-wrap items-center gap-1.5" data-testid="quest-color-picker">
                {QUEST_PALETTE.map((entry) => (
                  <button
                    key={entry.value}
                    type="button"
                    title={entry.label}
                    aria-label={entry.label}
                    aria-pressed={form.color === entry.value}
                    data-testid={`quest-color-${entry.value}`}
                    onClick={() => setField('color', form.color === entry.value ? '' : entry.value)}
                    className={`h-7 w-7 rounded-full border transition ${
                      form.color === entry.value
                        ? 'border-gray-900 ring-2 ring-gray-900/20'
                        : 'border-gray-300 hover:border-gray-500'
                    }`}
                    style={{ background: entry.swatch }}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500">
                {form.color
                  ? `${QUEST_PALETTE.find((entry) => entry.value === form.color)?.label ?? form.color} — click again to clear.`
                  : 'Blank = a stable color picked from the quest slug.'}
              </p>
            </div>
          </div>

          {/* What the member will actually see — icon and color together, in the
                band's own shape. */}
          <div className="space-y-1">
            <Label>How it looks on the band</Label>
            <div
              className="flex items-center gap-3 rounded-lg border p-3"
              data-testid="quest-identity-preview"
              style={{
                background: identityPalette.background,
                borderColor: identityPalette.borderColor,
              }}
            >
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl"
                style={{ background: identityPalette.tile }}
              >
                {form.emoji || questTypeEmoji(form.questType)}
              </span>
              <span className="min-w-0 text-sm font-semibold leading-snug text-gray-900">
                {form.heading || form.name}
              </span>
            </div>
          </div>

          {/* THE AWARD, AND WHAT IT SAYS.
              A quest name describes work — "Make some friends here, and let your work be
              seen" — and the profile's award card sets that as its hero line, at the
              largest type on a square certificate with no truncation. An award wants a
              noun somebody would be pleased to wear. Blank falls back to the quest name,
              so every existing quest keeps exactly the card it has today.

              The preview is the REAL card, the same image the profile and the share
              dialog render. An admin could not see what they were making before, which
              is how a field like this drifts from its own output. */}
          <div className="space-y-1">
            <Label htmlFor="q-award">Award title (what the card on their profile says)</Label>
            <Input
              id="q-award"
              value={form.awardTitle}
              maxLength={80}
              onChange={(e) => setField('awardTitle', e.target.value)}
              placeholder={form.name}
            />
            <p className="text-xs text-gray-500">
              Blank = the quest name. Short works best — it is set large and never truncates.
            </p>
          </div>

          <div className="space-y-1">
            <Label>The award on their profile</Label>
            <img
              src={questAwardPreviewUrl(form.awardTitle.trim() || form.name)}
              alt="Preview of the award card"
              data-testid="quest-award-preview"
              className="w-56 rounded-lg border border-gray-200 bg-gray-900"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>How members get this quest</Label>
              <Select value={form.assignment} onValueChange={(value) => setField('assignment', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automatic — assigned by user criteria</SelectItem>
                  <SelectItem value="optin">Opt-in only — joined via its link</SelectItem>
                  <SelectItem value="invite">Invite-only — invited members and its private link</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Public directory (/quests)</Label>
              <Select
                value={form.directoryVisible ? 'visible' : 'hidden'}
                onValueChange={(value) => setField('directoryVisible', value === 'visible')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="visible">Listed on the directory</SelectItem>
                  <SelectItem value="hidden">Hidden — link and joined members only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <QuestRecurrenceFields form={form} setField={setField} live={quest?.status === 'LIVE'} />
          {form.assignment === 'invite' && quest ? <QuestInvitePanel quest={quest} /> : null}
          {form.assignment === 'invite' ? null : form.assignment === 'optin' ? (
            <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
              Opt-in quests have no user criteria — members get them only through the opt-in link
              {form.directoryVisible ? ' or the public directory' : ''}.
            </p>
          ) : (
            <>
              <div className="space-y-1">
                <Label htmlFor="q-intents">
                  Audience intents (comma-separated; empty on every field = the default quest)
                </Label>
                <Input
                  id="q-intents"
                  value={form.intents}
                  onChange={(e) => setField('intents', e.target.value)}
                  placeholder="social, feedback"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="q-stages">Career stages</Label>
                  <Input
                    id="q-stages"
                    value={form.careerStages}
                    onChange={(e) => setField('careerStages', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="q-countries">Country codes</Label>
                  <Input
                    id="q-countries"
                    value={form.countryCodes}
                    onChange={(e) => setField('countryCodes', e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="q-priority">Priority (lowest wins among matching quests)</Label>
              <Input id="q-priority" value={form.priority} onChange={(e) => setField('priority', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="q-points">Point reward on completion (0 = none)</Label>
              <Input
                id="q-points"
                value={form.pointsReward}
                onChange={(e) => setField('pointsReward', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Quest type (groups the index page)</Label>
              <Select value={form.questType} onValueChange={(value) => setField('questType', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUEST_TYPES.map((entry) => (
                    <SelectItem key={entry.value} value={entry.value}>
                      {entry.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Gate step (top rung locked until it is done)</Label>
              <Select
                value={form.gateStepCode || 'none'}
                onValueChange={(value) => setField('gateStepCode', value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No gate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No gate</SelectItem>
                  {rows
                    .filter((row) => !isDayBreakStepCode(row.stepCode))
                    .map((row) => (
                      <SelectItem key={row.stepCode} value={row.stepCode}>
                        {catalog.get(row.stepCode)?.adminLabel ?? row.stepCode}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Steps (drag to set the guided path)
            </h2>
            <span className={`text-sm font-semibold ${enabledTotal === 100 ? 'text-green-700' : 'text-red-600'}`}>
              Weights: {enabledTotal}/100
            </span>
          </div>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={rows.map((row) => row.key)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {rows.map((row) => (
                  <SortableStepRow
                    key={row.key}
                    row={row}
                    catalog={catalog}
                    gate={form.gateStepCode === row.stepCode}
                    onWeight={(key, weight) =>
                      setRows((current) => current.map((r) => (r.key === key ? { ...r, weight } : r)))
                    }
                    onOverride={(key, field, value) =>
                      setRows((current) =>
                        current.map((r) =>
                          r.key === key ? { ...r, overrides: { ...r.overrides, [field]: value } } : r
                        )
                      )
                    }
                    onToggle={(key, enabled) =>
                      setRows((current) => current.map((r) => (r.key === key ? { ...r, enabled } : r)))
                    }
                    onRemove={(key) => setRows((current) => current.filter((r) => r.key !== key))}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <div className="flex items-center gap-2">
            <Select value={addCode} onValueChange={setAddCode}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Add a step from the catalog…" />
              </SelectTrigger>
              <SelectContent>
                {availableToAdd.map((entry) => (
                  <SelectItem key={entry.code} value={entry.code}>
                    {entry.adminLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              disabled={!addCode}
              onClick={() => {
                if (!addCode) return;
                setRows((current) => [
                  ...current,
                  { key: addCode, stepCode: addCode, weight: '0', enabled: true, overrides: {} },
                ]);
                setAddCode('');
              }}
            >
              <Plus className="mr-1 h-4 w-4" /> Add
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                /**
                 * `quest_steps` is unique on (questId, stepCode), so every custom step on
                 * a quest needs its own code — hence custom:1, custom:2. Numbered from the
                 * highest already present rather than the count, so removing the middle one
                 * and adding another cannot collide with a code still on the quest.
                 */
                const used = rows
                  .map((row) => Number(row.stepCode.match(/^custom:(\d+)$/)?.[1] ?? 0))
                  .filter((n) => Number.isFinite(n));
                const code = `custom:${Math.max(0, ...used) + 1}`;
                setRows((current) => [
                  ...current,
                  { key: code, stepCode: code, weight: '0', enabled: true, overrides: {} },
                ]);
              }}
              title="A step written for this quest: your own wording, link and behaviour"
            >
              <Plus className="mr-1 h-4 w-4" /> Custom step
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const code = nextDayBreakCode(rows.map((row) => row.stepCode));
                setRows((current) => [
                  ...current,
                  { key: code, stepCode: code, weight: '0', enabled: true, overrides: {} },
                ]);
              }}
              title="Split the quest across days: everything below this opens the day after the steps above it are finished"
            >
              <Plus className="mr-1 h-4 w-4" /> Day break
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            MEASURED steps check themselves from the member's real data and cannot be ticked by hand. ON CLICK steps
            check when the member clicks through to the tool, because the work happens where we cannot read the result
            back. A step's measurement is fixed in code; weight, order and enablement are yours. Weights on enabled
            steps must total exactly 100 before the quest can go live — the score is the sum of its parts. A DAY BREAK
            carries no weight: it splits the quest across days, and everything below one stays hidden until the day
            after the member finishes the steps above it.
          </p>
        </section>
      </div>

      {/* z-50, above the messaging dock's z-40. The dock is `fixed bottom-0 right-4` and
          this bar is full-width with no stacking of its own, so the dock sat ON the Save
          button: a real mouse click and a real touch tap both hit the dock instead, and
          the only way to save was to Tab to the button and press Enter. Found by testing
          with actual click mechanisms rather than a scripted element.click(), which
          bypasses hit-testing and reports success over an unreachable control. */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-white/90 px-6 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <span className="text-sm text-gray-600">
            {hasUnsavedChanges ? 'Unsaved changes · settings, order, weights and toggles' : 'All changes saved'}
          </span>
          <Button onClick={() => void handleSave()} disabled={!hasUnsavedChanges || savingSettings || savingSteps}>
            {savingSettings || savingSteps ? 'Saving…' : 'Save quest'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}

/**
 * How often the quest comes round, and how it reaches her (#4118). A live quest's cadence
 * is locked — changing it would re-decide who has finished. A recurring quest may only use
 * steps that cannot re-pay for nothing; the server names any that break that on save.
 */
function QuestRecurrenceFields({
  form,
  setField,
  live,
}: {
  form: SettingsForm;
  setField: (key: keyof SettingsForm, value: any) => void;
  live: boolean;
}) {
  // Reminders only mean something on a quest that comes round again.
  const period = form.cadence === 'once' ? null : (form.cadence as 'daily' | 'weekly' | 'monthly');
  return (
    <div className="space-y-3 rounded-md border border-gray-200 p-3" data-testid="quest-recurrence">
      <div className="space-y-1">
        <Label>How often it comes round</Label>
        <Select value={form.cadence} disabled={live} onValueChange={(value) => setField('cadence', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="once">Once — finished for good</SelectItem>
            <SelectItem value="daily">Every day</SelectItem>
            <SelectItem value="weekly">Every week</SelectItem>
            <SelectItem value="monthly">Every month</SelectItem>
          </SelectContent>
        </Select>
        {live ? <p className="text-xs text-gray-500">Set it to draft to change this.</p> : null}
      </div>
      <div className="divide-y divide-gray-100" data-testid="quest-channels">
        <ChannelRow
          title="Pin"
          detail="It may take the quest band at the top of the page."
          checked={form.channelPin}
          onCheckedChange={(checked) => setField('channelPin', checked)}
          testId="quest-channel-pin"
        />
        <ChannelRow
          title="Inbox"
          detail="A row in the member's inbox when the quest comes round again, from 08:00 their time."
          checked={form.channelNotify}
          onCheckedChange={(checked) => setField('channelNotify', checked)}
          testId="quest-channel-notify"
          reminder={
            period ? (
              <ReminderSelect
                channel="notify"
                value={form.notifyReminder}
                cadence={period}
                onChange={(value) => setField('notifyReminder', value)}
                testIdPrefix="quest"
              />
            ) : undefined
          }
        />
        <ChannelRow
          title="Email"
          detail="When a quest the member joined comes round again — at most one reward email a day, 08:00–21:00 their time."
          checked={form.channelEmail}
          onCheckedChange={(checked) => setField('channelEmail', checked)}
          testId="quest-channel-email"
          reminder={
            period ? (
              <ReminderSelect
                channel="email"
                value={form.emailReminder}
                cadence={period}
                onChange={(value) => setField('emailReminder', value)}
                testIdPrefix="quest"
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
}

/** Invite members, and the private link that is itself an invitation. */
function QuestInvitePanel({ quest }: { quest: AdminQuest }) {
  const [picked, setPicked] = useState<PickedMember | null>(null);
  const [inviteMembers] = useMutation(ADMIN_INVITE_TO_QUEST);
  const [rotate] = useMutation(ADMIN_ROTATE_QUEST_INVITE, { refetchQueries: ['AdminQuest'] });

  const invite = async () => {
    if (!picked) return;
    try {
      const result = await inviteMembers({ variables: { id: quest.id, userIds: [picked.asfId] } });
      toast.success(result.data.adminInviteToQuest ? 'Invited.' : 'Already invited.');
      setPicked(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to invite');
    }
  };

  const newLink = async () => {
    const result = await rotate({ variables: { id: quest.id } });
    await navigator.clipboard.writeText(questInviteUrl(quest.slug, result.data.adminRotateQuestInvite));
    toast.success('New link copied. The old link no longer works.');
  };

  return (
    <div className="space-y-3 rounded-md border border-gray-200 p-3" data-testid="quest-invite-panel">
      <p className="text-sm font-medium">Invite members</p>
      <MemberPicker picked={picked} onPick={setPicked} testId="quest-invite-search" />
      <Button size="sm" disabled={!picked} onClick={() => void invite()}>
        Invite
      </Button>
      <p className="text-xs text-gray-500">The link is the invitation. Making a new one stops the old one working.</p>
      {quest.inviteToken ? (
        <p className="break-all text-xs text-gray-700">{questInviteUrl(quest.slug, quest.inviteToken)}</p>
      ) : null}
      <Button size="sm" variant="outline" onClick={() => void newLink()}>
        {quest.inviteToken ? 'New link' : 'Make a link'}
      </Button>
    </div>
  );
}
